import React, { useCallback, useEffect, useState } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { EmptyState } from "../../components/Common";
import api, { apiErrorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Calendar, Users, Receipt, Activity, BarChart3 } from "lucide-react";
import { printInvoiceDocument } from "../../lib/invoicePrint";
import { AppointmentSlotPicker, SlotRescheduleModal } from "../../components/AppointmentSlotPicker";
import { ClinicBillingWorkspace } from "../../components/ClinicBillingWorkspace";
import { VisitTodayWorkspace } from "../../components/VisitTodayWorkspace";
import { useAuth } from "../../lib/auth";
import { PatientSearchSelect } from "../../components/PatientSearchSelect";
import { doctorOffersService, effectiveBookingPrice, normalizeBookingService, normalizeDoctorServiceAssignment } from "../../lib/bookingOptions";

export const links = [
  { to: "/reception", label: "Front Desk", icon: <BarChart3 className="w-4 h-4" />, end: true },
  { to: "/reception/book", label: "Book Appointment", icon: <Calendar className="w-4 h-4" /> },
  { to: "/reception/patients", label: "Patients", icon: <Users className="w-4 h-4" /> },
  { to: "/reception/billing", label: "Billing", icon: <Receipt className="w-4 h-4" /> },
];

export function ReceptionOverview() {
  return (
    <DashboardLayout links={links} title="Front Desk Overview">
      <VisitTodayWorkspace mode="reception" embedded />
    </DashboardLayout>
  );
}

export function ReceptionQueue() {
  return <DashboardLayout links={links} title="Visits & Walk-ins"><VisitTodayWorkspace mode="reception" embedded /></DashboardLayout>;
}

// Kept temporarily for reference while the new visit-aware billing workspace is built.
function LegacyReceptionQueue() {
  const [list, setList] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const refresh = useCallback(() => api.get("/appointments", { params: { date: today } }).then((r) => setList(r.data)), [today]);
  useEffect(() => { refresh(); }, [refresh]);
  const updateStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}/status?status=${encodeURIComponent(status)}`); toast.success(`Marked ${status}`); refresh(); }
    catch (e) { toast.error("Failed"); }
  };
  const [createInvFor, setCreateInvFor] = useState(null);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const cancelAppointment = async (appointmentId) => {
    const reason = window.prompt("Cancellation reason", "Patient requested cancellation");
    if (reason === null) return;
    try { await api.post(`/clinic/appointments-x/${appointmentId}/cancel`, { reason }); toast.success("Appointment cancelled"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel appointment"); }
  };
  const rescheduleAppointment = async (next, reason) => {
    setRescheduleBusy(true);
    try { await api.post(`/clinic/appointments-x/${rescheduleFor.appointment_id}/reschedule`, { new_datetime: next, reason: reason || "Front desk reschedule" }); toast.success("Appointment rescheduled"); setRescheduleFor(null); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule appointment"); }
    finally { setRescheduleBusy(false); }
  };

  const handleCreateInvoice = async (items, discount) => {
    try {
      await api.post("/billing/invoices", {
        patient_id: createInvFor.patient_id,
        appointment_id: createInvFor.appointment_id,
        items: items,
        discount: Number(discount) || 0
      });
      toast.success("Invoice Created");
      setCreateInvFor(null);
      refresh();
    } catch (e) { toast.error("Failed to create invoice"); }
  };

  return (
    <DashboardLayout links={links} title="Today's Queue">
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Time</th><th>Patient</th><th>Doctor</th><th>Service</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {list.map((a) => (
              <tr key={a.appointment_id} className="border-b border-slate-50" data-testid={`q-${a.appointment_id}`}>
                <td className="py-3 font-semibold">{new Date(a.appointment_datetime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                <td>{a.patient?.name}<div className="text-xs text-slate-500">{a.patient?.phone}</div></td>
                <td>{a.doctor?.name}</td>
                <td>{a.service?.name}</td>
                <td><span className={`mg-status mg-status-${a.status.toLowerCase().replace(/\s+/g, "-")}`}>{a.status}</span></td>
                <td className="text-right space-x-1">
                  {a.status === "Scheduled" || a.status === "Confirmed" ? (
                    <><button onClick={() => updateStatus(a.appointment_id, "Checked In")} className="text-xs bg-purple-100 text-purple-700 px-3 py-1.5 rounded-lg" data-testid={`checkin-${a.appointment_id}`}>Check in</button><button onClick={() => setRescheduleFor(a)} className="text-xs text-mg-medical px-2">Reschedule</button><button onClick={() => cancelAppointment(a.appointment_id)} className="text-xs text-red-600 px-2">Cancel</button></>
                  ) : a.status === "Checked In" ? (
                    <span className="text-xs text-amber-700 font-semibold">Waiting for doctor</span>
                  ) : a.status === "In Consultation" ? (
                    <span className="text-xs text-mg-medical font-semibold">Doctor consulting</span>
                  ) : a.status === "Completed" && a.payment_status === "none" ? (
                    <button onClick={() => setCreateInvFor(a)} className="text-xs bg-mg-health text-white px-3 py-1.5 rounded-lg flex items-center gap-1"><Receipt className="w-3 h-3" />Bill</button>
                  ) : a.status === "Completed" && a.payment_status !== "paid" ? (
                    <span className="text-xs text-amber-600 font-semibold flex items-center gap-1 justify-end"><Activity className="w-3 h-3" /> Pending Payment</span>
                  ) : null}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-400 text-sm">No appointments today.</td></tr>}
          </tbody>
        </table>
      </div>
      {createInvFor && <CreateInvoiceModal appt={createInvFor} onClose={() => setCreateInvFor(null)} onSubmit={handleCreateInvoice} />}
      {rescheduleFor && <SlotRescheduleModal appointment={rescheduleFor} onClose={() => setRescheduleFor(null)} onSubmit={rescheduleAppointment} busy={rescheduleBusy} />}
    </DashboardLayout>
  );
}

function CreateInvoiceModal({ appt, onClose, onSubmit }) {
  const [items, setItems] = useState([{ description: "Consultation Fee", quantity: 1, unit_price: 500, tax_rate: 0 }]);
  const [discount, setDiscount] = useState(0);

  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, tax_rate: 0 }]);
  const updateItem = (idx, field, val) => {
    const newItems = [...items];
    newItems[idx][field] = val;
    setItems(newItems);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const subtotal = items.reduce((sum, it) => sum + (it.quantity * it.unit_price * (1 + it.tax_rate/100)), 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
        <h3 className="font-bold text-mg-navy text-lg mb-4">Create Invoice for {appt.patient?.name}</h3>
        <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
          {items.map((it, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input className="mg-input flex-1" placeholder="Description" value={it.description} onChange={e => updateItem(idx, "description", e.target.value)} />
              <input className="mg-input w-20" type="number" placeholder="Qty" value={it.quantity} onChange={e => updateItem(idx, "quantity", Number(e.target.value))} />
              <input className="mg-input w-24" type="number" placeholder="Price" value={it.unit_price} onChange={e => updateItem(idx, "unit_price", Number(e.target.value))} />
              {items.length > 1 && <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700">x</button>}
            </div>
          ))}
          <button onClick={addItem} className="text-xs text-mg-medical hover:underline">+ Add Item</button>
        </div>
        <div className="mt-4 border-t pt-4 space-y-2">
           <div className="flex justify-between items-center text-sm">
             <span>Discount</span>
             <input className="mg-input w-24 text-right" type="number" value={discount} onChange={e => setDiscount(Number(e.target.value))} />
           </div>
           <div className="flex justify-between items-center font-bold text-mg-navy">
             <span>Total</span>
             <span>₹{Math.max(0, subtotal - discount).toFixed(2)}</span>
           </div>
        </div>
        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="mg-btn-secondary flex-1">Cancel</button>
          <button onClick={() => onSubmit(items, discount)} className="mg-btn-primary flex-1">Create Invoice</button>
        </div>
      </div>
    </div>
  );
}

export function ReceptionBook() {
  const { user } = useAuth();
  const [booking, setBooking] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctorServices, setDoctorServices] = useState([]);
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", service_id: "", appointment_datetime: "", notes: "" });
  const [newPat, setNewPat] = useState(null); // {name, phone, ...}
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const rows = (value) => Array.isArray(value) ? value : (value?.items || value?.data || []);
  useEffect(() => {
    api.get("/visits/options")
      .then((options) => {
        const data = options.data || {};
        setDoctors(rows(data.doctors));
        setServices(rows(data.services).map(normalizeBookingService));
        setDoctorServices(rows(data.doctor_services).map(normalizeDoctorServiceAssignment));
        setPatients(rows(data.patients));
        setLoadError(false);
      })
      .catch((error) => {
        if (error?.code !== "ERR_CANCELED" && error?.name !== "CanceledError") setLoadError(true);
      });
  }, []);
  useEffect(() => {
    if (!newPat?.phone && !newPat?.email && !(newPat?.name && newPat?.date_of_birth)) { setDuplicates([]); return; }
    const timer = setTimeout(() => {
      setCheckingDuplicates(true);
      api.get("/clinic/patients-x/duplicate-check", { params: newPat })
        .then((r) => setDuplicates(r.data.matches || []))
        .catch(() => setDuplicates([]))
        .finally(() => setCheckingDuplicates(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [newPat]);
  const availableDoctors = form.service_id
    ? doctors.filter((doctor) => doctorOffersService(doctorServices, doctor.doctor_id, form.service_id))
    : [];
  const selectExistingPatient = async (match) => {
    try {
      const patient = match.is_current_branch ? match : (await api.post(`/clinic/patients-x/${match.patient_id}/link-to-current-branch`)).data;
      setPatients((current) => current.some((row) => row.patient_id === patient.patient_id) ? current : [...current, patient]);
      setForm((current) => ({ ...current, patient_id: patient.patient_id }));
      setNewPat(null); setDuplicates([]);
      toast.success("Existing patient selected");
    } catch (e) { toast.error(apiErrorMessage(e, "Unable to use existing patient")); }
  };
  const book = async () => {
    if (booking) return;
    setBooking(true);
    try {
      if (!selectedSlot) return toast.error("Choose an available appointment slot");
      const payload = { ...form, appointment_datetime: selectedSlot.start };
      if (!form.patient_id && newPat) { payload.new_patient = newPat; delete payload.patient_id; }
      await api.post("/appointments", payload);
      toast.success("Appointment booked");
      setForm({ patient_id: "", doctor_id: "", service_id: "", appointment_datetime: "", notes: "" });
      setNewPat(null); setSelectedSlot(null); setDuplicates([]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.duplicates) setDuplicates(detail.duplicates);
      toast.error(apiErrorMessage(e, "Failed"));
    } finally { setBooking(false); }
  };
  return (
    <DashboardLayout links={links} title="Book Appointment">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-3">Appointment details</h3>
          {loadError && <p className="text-sm text-red-600 mb-3">Booking information could not be loaded. Please refresh and try again.</p>}
          <div className="space-y-3">
            <PatientSearchSelect patients={patients} value={form.patient_id} onChange={(patient_id) => { setForm({ ...form, patient_id }); setNewPat(null); }} label="Existing patient" testid="book-patient" />
            <select className="mg-input" value={form.service_id} onChange={(e) => { setSelectedSlot(null); setForm({ ...form, service_id: e.target.value, doctor_id: "" }); }} data-testid="book-service">
              <option value="">Select service…</option>
              {services.map((s) => { const text = `${s.name} — ₹${s.price} (${s.duration_minutes} min)`; return <option key={s.service_id} value={s.service_id} label={text}>{text}</option>; })}
            </select>
            <select className="mg-input" value={form.doctor_id} onChange={(e) => { setSelectedSlot(null); setForm({ ...form, doctor_id: e.target.value }); }} data-testid="book-doctor" disabled={!form.service_id}>
              <option value="">Select doctor…</option>
              {availableDoctors.map((d) => { const text = `${d.name} — ${d.specialization || "General"} — ₹${effectiveBookingPrice(services, doctorServices, d.doctor_id, form.service_id)}`; return <option key={d.doctor_id} value={d.doctor_id} label={text}>{text}</option>; })}
            </select>
            {form.service_id && !loadError && availableDoctors.length === 0 && <p role="alert" className="text-sm text-amber-700">No doctor is assigned to this service. Ask your clinic administrator to configure doctor services before booking.</p>}
            {form.doctor_id && form.service_id && <AppointmentSlotPicker clinicId={user.clinic_id} doctorId={form.doctor_id} serviceId={form.service_id} onSelect={setSelectedSlot} />}
            {selectedSlot && <p className="text-sm font-semibold text-emerald-700">Selected: {selectedSlot.local_start?.replace("T", " ").slice(0, 16)} ({selectedSlot.timezone})</p>}
            <textarea className="mg-input" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <button onClick={book} disabled={booking || !selectedSlot || (!form.patient_id && (!newPat?.name?.trim() || !newPat?.phone?.trim()))} className="mg-btn-primary w-full justify-center" data-testid="book-submit">Book appointment</button>
          </div>
        </div>
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-1">Or add a new patient</h3>
          <p className="text-xs text-slate-500 mb-3">Use this when the person is not already registered. The new patient and appointment are created together.</p>
          <div className="space-y-3">
            <input className="mg-input" placeholder="Patient name" value={newPat?.name || ""} onChange={(e) => setNewPat({ ...(newPat || {}), name: e.target.value })} data-testid="walkin-name" />
            <input className="mg-input" placeholder="Phone" value={newPat?.phone || ""} onChange={(e) => setNewPat({ ...(newPat || {}), phone: e.target.value })} data-testid="walkin-phone" />
            <input className="mg-input" placeholder="Email (optional)" value={newPat?.email || ""} onChange={(e) => setNewPat({ ...(newPat || {}), email: e.target.value })} />
            <input type="date" className="mg-input" aria-label="Date of birth (optional)" value={newPat?.date_of_birth || ""} onChange={(e) => setNewPat({ ...(newPat || {}), date_of_birth: e.target.value })} />
            <select className="mg-input" value={newPat?.gender || "Male"} onChange={(e) => setNewPat({ ...(newPat || {}), gender: e.target.value })}>
              <option>Male</option><option>Female</option><option>Other</option>
            </select>
            <div className="text-xs text-slate-500">Fill these to create a new patient; the appointment will use this new patient instead of the dropdown.</div>
            {checkingDuplicates && <p className="text-xs text-slate-500">Checking organization records…</p>}
            {duplicates.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" role="alert">
              <p className="text-sm font-semibold text-amber-900">Possible existing patient found</p>
              {duplicates.map((match) => <div key={match.patient_id} className="flex items-center justify-between gap-2 mt-2 text-sm">
                <span>{match.name} · {match.branch_name} · matched {match.match_reasons.join(", ")}</span>
                <button type="button" className="mg-btn-secondary text-xs" onClick={() => selectExistingPatient(match)}>Use patient</button>
              </div>)}
            </div>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function ReceptionPatients() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/clinic/patients", { params: { q } }).then((r) => setList(r.data)); }, [q]);
  return (
    <DashboardLayout links={links} title="Patients">
      <input className="mg-input mb-4 max-w-md" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Name</th><th>Phone</th><th>Email</th><th>Gender</th></tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.patient_id} className="border-b border-slate-50">
                <td className="py-3 font-semibold text-mg-navy"><a href={`/reception/patients/${p.patient_id}`} className="hover:text-mg-medical hover:underline">{p.name}</a></td>
                <td>{p.phone}</td><td>{p.email || "—"}</td><td>{p.gender || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function ReceptionBilling() {
  return <DashboardLayout links={links} title="Billing"><ClinicBillingWorkspace /></DashboardLayout>;
}

function LegacyReceptionBilling() {
  const [invoices, setInvoices] = useState([]);
  const [modal, setModal] = useState(null);
  const [createInvFor, setCreateInvFor] = useState(false);
  const [patients, setPatients] = useState([]);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const refresh = () => api.get("/billing/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { 
    refresh(); 
    api.get("/clinic/patients-x").then(r => setPatients(r.data.items || r.data)).catch(()=>{});
  }, []);

  const recordPayment = async () => {
    try { await api.post(`/billing/invoices/${modal.invoice_id}/payments`, { amount: Number(amount), method }); toast.success("Payment recorded"); setModal(null); setAmount(""); refresh(); }
    catch (e) { toast.error("Failed"); }
  };

  const handleCreateInvoice = async (items, discount, manualPatientId) => {
    try {
      await api.post("/billing/invoices", {
        patient_id: manualPatientId,
        items: items,
        discount: Number(discount) || 0
      });
      toast.success("Invoice Created");
      setCreateInvFor(false);
      refresh();
    } catch (e) { toast.error("Failed to create invoice"); }
  };

  const legacyPrintInvoice = (inv) => {
     const printWindow = window.open("", "_blank");
     printWindow.document.write(`
       <html><head><title>Invoice ${inv.invoice_number}</title></head><body style="font-family: sans-serif; padding: 40px;">
       <h2>Invoice: ${inv.invoice_number}</h2>
       <p>Date: ${new Date(inv.invoice_date).toLocaleDateString()}</p>
       <p>Status: <strong>${inv.status}</strong></p>
       <hr/>
       <table width="100%" border="1" cellspacing="0" cellpadding="8">
         <tr><th>Item</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
         ${inv.items.map(it => `<tr><td>${it.description}</td><td>${it.quantity}</td><td>${it.unit_price}</td><td>${it.total}</td></tr>`).join('')}
       </table>
       <br/>
       <p><strong>Subtotal:</strong> ₹${inv.subtotal}</p>
       <p><strong>Discount:</strong> ₹${inv.discount}</p>
       <p><strong>Tax:</strong> ₹${inv.tax_amount}</p>
       <p><strong>Total:</strong> ₹${inv.total}</p>
       <p><strong>Paid Amount:</strong> ₹${inv.paid_amount}</p>
       <p><strong>Balance Amount:</strong> ₹${inv.balance_amount}</p>
       <script>window.print(); window.close();</script>
       </body></html>
     `);
     printWindow.document.close();
  };
  const printInvoice = (inv) => {
    try { printInvoiceDocument(inv); } catch (error) { toast.error(error.message); }
  };

  return (
    <DashboardLayout links={links} title="Billing">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-mg-navy">All Invoices</h3>
        <button onClick={() => setCreateInvFor(true)} className="mg-btn-primary text-sm flex items-center gap-1">+ Create Invoice</button>
      </div>
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Invoice</th><th>Patient</th><th className="text-right">Total</th><th className="text-right">Paid</th><th>Status</th><th>Actions</th><th></th></tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.invoice_id} className="border-b border-slate-50">
                <td className="py-3 font-semibold text-mg-navy">{i.invoice_number}</td>
                <td>{i.patient?.name}</td>
                <td className="text-right font-semibold">₹{i.total}</td>
                <td className="text-right text-mg-health font-semibold">₹{i.paid_amount}</td>
                <td><span className={`mg-status mg-status-${i.status.toLowerCase()}`}>{i.status}</span></td>
                <td>
                  <button onClick={() => printInvoice(i)} className="text-mg-medical text-xs font-semibold hover:underline">Print / Save PDF</button>
                </td>
                <td className="text-right">
                  {i.status !== "Paid" && <button onClick={() => { setModal(i); setAmount(String(i.balance_amount)); }} className="text-xs bg-mg-medical text-white px-3 py-1.5 rounded-lg" data-testid={`pay-${i.invoice_id}`}>Collect</button>}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan="7" className="py-8 text-center text-slate-400 text-sm">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-mg-navy text-lg mb-1">Record payment</h3>
            <div className="text-sm text-slate-500 mb-4">{modal.invoice_number} — {modal.patient?.name}</div>
            <input type="number" className="mg-input mb-3" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <select className="mg-input" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option>Cash</option><option>Card</option><option>UPI</option><option>Online</option><option>Insurance</option><option>Other</option>
            </select>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setModal(null)} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={recordPayment} className="mg-btn-primary flex-1 justify-center">Record</button>
            </div>
          </div>
        </div>
      )}
      {createInvFor && <CreateManualInvoiceModal patients={patients} onClose={() => setCreateInvFor(false)} onSubmit={handleCreateInvoice} />}
    </DashboardLayout>
  );
}

function CreateManualInvoiceModal({ patients, onClose, onSubmit }) {
  const [patientId, setPatientId] = useState("");
  const [items, setItems] = useState([{ description: "Consultation Fee", quantity: 1, unit_price: 500, tax_rate: 0 }]);
  const [discount, setDiscount] = useState(0);

  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, tax_rate: 0 }]);
  const updateItem = (idx, field, val) => {
    const newItems = [...items];
    newItems[idx][field] = val;
    setItems(newItems);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const subtotal = items.reduce((sum, it) => sum + (it.quantity * it.unit_price * (1 + it.tax_rate/100)), 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
        <h3 className="font-bold text-mg-navy text-lg mb-4">Create Manual Invoice</h3>
        <div className="mb-4">
          <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Patient</label>
          <PatientSearchSelect patients={patients} value={patientId} onChange={setPatientId} required />
        </div>
        <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
          {items.map((it, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input className="mg-input flex-1" placeholder="Description" value={it.description} onChange={e => updateItem(idx, "description", e.target.value)} />
              <input className="mg-input w-20" type="number" placeholder="Qty" value={it.quantity} onChange={e => updateItem(idx, "quantity", Number(e.target.value))} />
              <input className="mg-input w-24" type="number" placeholder="Price" value={it.unit_price} onChange={e => updateItem(idx, "unit_price", Number(e.target.value))} />
              {items.length > 1 && <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700">x</button>}
            </div>
          ))}
          <button onClick={addItem} className="text-xs text-mg-medical hover:underline">+ Add Item</button>
        </div>
        <div className="mt-4 border-t pt-4 space-y-2">
           <div className="flex justify-between items-center text-sm">
             <span>Discount</span>
             <input className="mg-input w-24 text-right" type="number" value={discount} onChange={e => setDiscount(Number(e.target.value))} />
           </div>
           <div className="flex justify-between items-center font-bold text-mg-navy">
             <span>Total</span>
             <span>₹{Math.max(0, subtotal - discount).toFixed(2)}</span>
           </div>
        </div>
        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="mg-btn-secondary flex-1">Cancel</button>
          <button onClick={() => {
            if(!patientId) return toast.error("Select patient");
            onSubmit(items, discount, patientId);
          }} className="mg-btn-primary flex-1">Create Invoice</button>
        </div>
      </div>
    </div>
  );
}
